import { z } from "zod";

/**
 * One error shape, everywhere. `message` is written for a person and may be
 * rendered in the UI unmodified; internal error text never reaches the client,
 * because a finance app's stack trace names tables and columns.
 */

export const ErrorCode = z.enum([
  "validation_failed",
  "unauthenticated",
  "forbidden",
  "not_found",
  "conflict",
  "unprocessable",
  "rate_limited",
  "internal",
]);

export const ApiError = z.object({
  error: z.object({
    code: ErrorCode,
    message: z.string(),
    details: z
      .array(z.object({ path: z.array(z.union([z.string(), z.number()])), message: z.string() }))
      .optional(),
    requestId: z.string(),
  }),
});

export type ErrorCode = z.infer<typeof ErrorCode>;
export type ApiError = z.infer<typeof ApiError>;

/**
 * 404 covers "not found" and "not yours" alike, and the two are never
 * distinguished — telling someone a row exists but is not theirs confirms the
 * row exists.
 */
export const STATUS_BY_CODE: Record<ErrorCode, number> = {
  validation_failed: 400,
  unauthenticated: 401,
  forbidden: 403,
  not_found: 404,
  conflict: 409,
  unprocessable: 422,
  rate_limited: 429,
  internal: 500,
};
